// オート周回（GAME_DESIGN 20章）。Google連携したアカウントなら無料（未クリアへ進むのも。旧「オート」の権利は販売終了）。画面を開いている間
// （閉じてから10分まではサーバーが計算する：POST /api/farm/settle）、
// 未クリアへ進む → 負けたら周回先で周回 → 指定回数ごとに再挑戦、を繰り返す（再挑戦「しない」なら周回先だけを回る）。
// 各戦は通常どおり POST /api/battle/start（auto:true）→ 端末で戦闘 → 1戦の時間（手番数×秒÷倍速）を待つ → /api/battle/finish（サーバー検証）。
// 兵糧が尽きたら止めずに回復（4分で1）を待つ。ここは「次にどこへ行くか」と「いつ止めるか」だけを決める。戦闘の進行は screens/battle.ts。
import type { CharId } from '@hyakki/core';
import { api, type FarmSettleResponse, type FinishResponse } from './api';
import { hyoroCost, powerOf, stageById } from './core-bridge';
import { isCleared, levelNow, state } from './state';
import type { AutoHaul, HaulDrop } from './ui/haul-fx';

export type AutoLimit = { kind: 'none' } | { kind: 'count'; n: number } | { kind: 'until'; at: number };

export interface AutoRunConfig {
  charId: CharId;
  /** 周回先（null＝直近の勝利先。選択中の戦場は開始時に番号にして入れる） */
  target: number | null;
  /** 周回を何回したら未クリアへ再挑戦するか（0＝しない：周回先だけを回る） */
  retryEvery: number;
  limit: AutoLimit;
  /** 節電モード：盤面を描かない（1戦の時間は同じ） */
  fast: boolean;
  /** オートの目標（式神候補を必ず倒す・龍脈をすべて取る） */
  ai: { shiki: boolean; relays: boolean };
  /** 負けたとき：止める／ひとつ手前の戦場に戻って周回（勝ちを重ねたら戻る）／同じ戦場に再挑戦（retryTimes 回続けて負けたら止める） */
  onLose?: 'stop' | 'back' | 'retry';
  retryTimes?: number;
  /** 物語の場面（ADV）が来たら止めて見る。false ならスキップ（見ていない場面は、あとで手動で出陣したときに出る） */
  story?: boolean;
}

export interface AutoRunLog {
  stageId: number;
  win: boolean;
  exp: number;
  zeni: number;
}

export interface AutoRun {
  cfg: AutoRunConfig;
  phase: 'advance' | 'farm';
  farmCount: number;
  farmLosses: number;
  battles: number;
  wins: number;
  losses: number;
  lastWin: number | null;
  startedAt: number;
  endedAt: number | null;
  stopReason: string | null;
  /** 続けて負けた回数 */
  loseStreak?: number;
  /** 負けて「ひとつ手前に戻る」ときの周回先（BACK_WINS 回勝ったら元へ） */
  backStage?: number | null;
  /** 「このバトルで周回を終わる」：今の1戦が決着したら止める */
  stopAfter?: boolean;
  /** 途中の1戦を保存してTOPへ（周回は止めず、「戦闘に戻る」でその1戦から続ける） */
  parked?: boolean;
  gains: {
    exp: number;
    zeni: number;
    zaimoku: number;
    reidama: number;
    drops: number;
    stars: number;
    levelUps: number;
    omamori: number;
    /** ドロップした装備（1つずつ。売却されたものも sold 付きで残す） */
    items: HaulDrop[];
    /** 加入・★が上がった式神（敵ID → 周回後の★） */
    shiki: Record<string, number>;
  };
  /** 開始時と直近の主Lv・戦力（周回明けのまとめ演出用） */
  start: { level: number; power: number | null };
  end: { level: number; power: number | null };
  log: AutoRunLog[];
}

/** まとめに残す装備の上限（長時間の周回でも重くしない） */
const ITEMS_KEEP = 1500;

/** 周回先で続けて負けたら止める回数 */
const FARM_LOSS_LIMIT = 3;
/** 「ひとつ手前に戻る」：手前の戦場でこの回数勝ったら、元の戦場へ挑み直す */
const BACK_WINS = 5;

let run: AutoRun | null = null;
const listeners = new Set<() => void>();
// 周回の集計は端末にも保存する（再読み込み・閉じたあとも、それまでの全戦のまとめを見せられるように）
const RUN_KEY = 'hk.autorunState';
const saveRun = () => {
  try {
    if (run && !run.endedAt) localStorage.setItem(RUN_KEY, JSON.stringify(run));
    else localStorage.removeItem(RUN_KEY);
  } catch {
    /* 保存できなくても周回は続ける */
  }
};
const emit = () => {
  saveRun();
  listeners.forEach((f) => f());
};
/** 再読み込み前に動いていた周回の集計（開き直したときに、閉じていたあいだの分と合わせてまとめを出す） */
export function takeInterruptedRun(): AutoRun | null {
  try {
    const raw = localStorage.getItem(RUN_KEY);
    localStorage.removeItem(RUN_KEY);
    const r = raw ? (JSON.parse(raw) as AutoRun) : null;
    return r && !r.endedAt && r.battles > 0 ? r : null;
  } catch {
    return null;
  }
}
export const onAutoRun = (fn: () => void) => (listeners.add(fn), () => void listeners.delete(fn));

/** 実行中のオート周回（止まったものは summary() で見る） */
export const autoRun = () => (run && !run.endedAt ? run : null);
/** 直近のオート周回（止まったあとも残る。リザルトでまとめて見せる） */
export const lastAutoRun = () => run;
export const dismissAutoRun = () => {
  if (run?.endedAt) run = null;
  emit();
};

function newRun(cfg: AutoRunConfig): AutoRun {
  return {
    cfg,
    phase: cfg.retryEvery > 0 ? 'advance' : 'farm',
    farmCount: 0,
    farmLosses: 0,
    battles: 0,
    wins: 0,
    losses: 0,
    lastWin: null,
    startedAt: Date.now(),
    endedAt: null,
    stopReason: null,
    gains: { exp: 0, zeni: 0, zaimoku: 0, reidama: 0, drops: 0, stars: 0, levelUps: 0, omamori: 0, items: [], shiki: {} },
    start: { level: levelNow(), power: powerOf(state.profile, cfg.charId) },
    end: { level: levelNow(), power: powerOf(state.profile, cfg.charId) },
    log: [],
  };
}

// 周回を動かしているタブの印（同じブラウザの別タブで開いたとき、閉じたものと思って周回を精算・放棄しないように）
const ALIVE_KEY = 'hk.autorunAlive';
const ALIVE_MS = 5_000;
let aliveTimer: ReturnType<typeof setInterval> | null = null;
const markAlive = (on: boolean) => {
  try {
    if (on) localStorage.setItem(ALIVE_KEY, String(Date.now()));
    else localStorage.removeItem(ALIVE_KEY);
  } catch {
    /* 印を残せなくても周回は続ける */
  }
};
function setAlive(on: boolean) {
  if (aliveTimer) clearInterval(aliveTimer);
  aliveTimer = on ? setInterval(() => markAlive(!!autoRun()), ALIVE_MS) : null;
  markAlive(on);
}
// 閉じる・再読み込みでは印を消す（開き直したタブで精算できるように）
addEventListener('pagehide', () => autoRun() && markAlive(false));
addEventListener('pageshow', () => autoRun() && markAlive(true));
/** ほかのタブが周回を動かしている */
export function autoRunElsewhere(): boolean {
  if (autoRun()) return false;
  try {
    const t = Number(localStorage.getItem(ALIVE_KEY) ?? 0);
    // 裏に回ったタブはブラウザがタイマーを最大1分間隔に絞るので、余裕を持って2分半（閉じる・再読み込みでは pagehide で印を消す）
    return Date.now() - t < 150_000;
  } catch {
    return false;
  }
}

export function startAutoRun(cfg: AutoRunConfig) {
  run = newRun(cfg);
  setAlive(true);
  emit();
}

/** 止める（サーバーにも伝えて、閉じたあとの猶予を付けない） */
/** 「このバトルで周回を終わる」の入／切 */
export function setStopAfter(on: boolean) {
  if (!run || run.endedAt) return;
  run.stopAfter = on;
  emit();
}

export function stopAutoRun(reason: string) {
  if (!run || run.endedAt) return;
  run.endedAt = Date.now();
  run.stopReason = reason;
  setAlive(false);
  void api.farmStop().catch(() => {});
  emit();
}

/** 次に出撃する戦場（兵糧が足りなければ waitMs だけ回復を待つ）。止めるべきなら理由を返す（呼び出し側が stopAutoRun する） */
export function nextAutoStage(): { stageId: number; waitMs?: number } | { stop: string } {
  const r = autoRun();
  if (!r) return { stop: '停止しました' };
  const lim = r.cfg.limit;
  if (lim.kind === 'count' && r.battles >= lim.n) return { stop: `指定の${lim.n}戦を終えました` };
  if (lim.kind === 'until' && Date.now() >= lim.at) return { stop: '指定の終了時刻になりました' };
  const prog = state.progress;
  // 未クリアの最前線＝出撃できる未クリアの戦場で一番弱いもの（国盗り。全部勝利済みなら無し → 周回先だけを回る）
  const frontier = prog?.next != null && !isCleared(prog.next) ? prog.next : null;
  const highest = Math.max(0, ...(prog?.cleared ?? []));
  const farm = r.backStage ?? r.cfg.target ?? r.lastWin ?? (highest || null);
  let stageId: number | null;
  if (r.phase === 'advance' && frontier && r.backStage == null) stageId = frontier;
  else {
    if (!frontier) r.phase = 'farm';
    if (!farm) return { stop: 'クリア済みの戦場がないため周回できません' };
    stageId = farm;
  }
  const st = stageById(stageId);
  if (!st) return { stop: '戦場が見つかりません' };
  const have = state.stamina ? state.stamina.value + state.stamina.reserve : (state.resources.hyoro ?? 0);
  if (have < hyoroCost(st, 0)) {
    const next = state.stamina?.nextAt;
    return { stageId, waitMs: next ? Math.max(1000, next - Date.now() + 500) : 4 * 60_000 };
  }
  return { stageId };
}

/** 1戦の結果を記録して、次の方針（前進／周回）を決める */
export function recordAutoBattle(stageId: number, win: boolean, server: FinishResponse | null) {
  const r = autoRun();
  if (!r) return;
  recordInto(r, stageId, win, server);
  emit();
}

/** 閉じていたあいだ（サーバーが計算した猶予の周回）の結果を、実行中の周回に足す */
export function recordSettledRuns(res: FarmSettleResponse) {
  const r = autoRun();
  if (!r) return;
  for (const x of res.runs) recordInto(r, x.stageId, x.win, { rewards: x.rewards, applied: x.applied } as unknown as FinishResponse, true);
  r.end = { level: res.level ?? levelNow(), power: powerOf(state.profile, r.cfg.charId) ?? r.end.power };
  emit();
}

/** 再読み込み前の周回の集計に、閉じていたあいだ（サーバーが計算した分）を足して、まとめの形にする */
export function haulOfInterrupted(prev: AutoRun, res: FarmSettleResponse | null): AutoHaul | null {
  for (const x of res?.runs ?? []) recordInto(prev, x.stageId, x.win, { rewards: x.rewards, applied: x.applied } as unknown as FinishResponse, true);
  prev.end = { level: res?.level ?? levelNow(), power: powerOf(state.profile, prev.cfg.charId) ?? prev.end.power };
  prev.endedAt = Date.now();
  prev.stopReason = '再読み込み・閉じたため停止しました';
  run = prev;
  emit();
  return autoHaulOf(prev);
}

/** 周回をしていない画面で受け取った猶予の周回を、まとめ演出の形にする（before＝受け取る前の主Lv・戦力） */
export function haulOfSettled(res: FarmSettleResponse, before: { level: number; power: number | null }): AutoHaul | null {
  if (!res.runs.length || !res.charId) return null;
  const r = newRun({ charId: res.charId, target: res.stageId ?? null, retryEvery: 0, limit: { kind: 'none' }, fast: false, ai: { shiki: false, relays: false } });
  r.start = before;
  for (const x of res.runs) recordInto(r, x.stageId, x.win, { rewards: x.rewards, applied: x.applied } as unknown as FinishResponse, true);
  r.end = { level: res.level ?? levelNow(), power: powerOf(state.profile, res.charId) ?? before.power };
  r.endedAt = Date.now();
  r.startedAt = r.endedAt - Math.max(1, res.minutes) * 60_000;
  r.stopReason = '閉じていたあいだの周回';
  return autoHaulOf(r);
}

/** 掃討の結果を、周回の成果と同じ形に */
export function haulOfSweep(res: Pick<FarmSettleResponse, 'runs' | 'level'>, charId: CharId, stageId: number, before: { level: number; power: number | null }): AutoHaul | null {
  if (!res.runs.length) return null;
  const r = newRun({ charId, target: stageId, retryEvery: 0, limit: { kind: 'none' }, fast: false, ai: { shiki: false, relays: false } });
  r.start = before;
  for (const x of res.runs) recordInto(r, x.stageId, x.win, { rewards: x.rewards, applied: x.applied } as unknown as FinishResponse, true);
  r.end = { level: res.level ?? levelNow(), power: powerOf(state.profile, charId) ?? before.power };
  r.endedAt = r.startedAt = Date.now();
  r.stopReason = '掃討';
  const h = autoHaulOf(r);
  return h && { ...h, title: '掃討の成果', minutes: 0 };
}

function recordInto(r: AutoRun, stageId: number, win: boolean, server: FinishResponse | null, settled = false) {
  r.battles++;
  if (win) r.wins++;
  else r.losses++;
  const rw = server?.rewards;
  const exp = rw?.exp ?? 0;
  const zeni = rw?.zeni ?? 0;
  r.gains.exp += exp;
  r.gains.zeni += zeni;
  r.gains.zaimoku += rw?.zaimoku ?? 0;
  r.gains.drops += rw?.equipment?.length ?? 0;
  r.gains.stars += rw?.firstStars?.length ?? 0;
  r.gains.reidama += rw?.reidama ?? 0;
  r.gains.omamori += rw?.omamori?.length ?? 0;
  collectDrops(r, server);
  // 1＝新しく仲間に、0＝また来た（霊玉）。同じ回に両方なら新しい方
  for (const s of rw?.shikigami ?? []) if (s.recruited) r.gains.shiki[s.enemyId] = Math.max(r.gains.shiki[s.enemyId] ?? 0, s.dup ? 0 : 1);
  const lvAfter = typeof server?.level === 'number' ? server.level : server?.level?.level;
  if (server?.levelBefore != null && lvAfter != null) r.gains.levelUps += Math.max(0, lvAfter - server.levelBefore);
  // 戦果のあとに refreshMe 済み（battle.ts）なので、ここでの主Lv・戦力が周回の「今」
  if (!settled) r.end = { level: lvAfter ?? levelNow(), power: powerOf(state.profile, r.cfg.charId) ?? r.end.power };
  r.log.unshift({ stageId, win, exp, zeni });
  r.log.length = Math.min(r.log.length, 30);
  if (win) r.lastWin = stageId;
  if (settled) return;
  const onLose = r.cfg.onLose ?? (r.cfg.retryEvery > 0 ? 'back' : 'retry');
  if (win) {
    r.loseStreak = 0;
    // ひとつ手前で勝ちを重ねたら、元の戦場へ挑み直す
    if (r.backStage != null && ++r.farmCount >= BACK_WINS) {
      r.backStage = null;
      r.farmCount = 0;
      if (r.cfg.retryEvery > 0) r.phase = 'advance';
    }
    return;
  }
  r.loseStreak = (r.loseStreak ?? 0) + 1;
  if (onLose === 'stop') return void stopAutoRun('負けたため停止しました');
  if (onLose === 'retry') {
    const n = Math.max(1, r.cfg.retryTimes ?? 3);
    if (r.loseStreak >= n) stopAutoRun(`${n}回続けて負けたため停止しました`);
    return;
  }
  // ひとつ手前に戻る（手前でも続けて負けたら止める）
  if (r.loseStreak >= FARM_LOSS_LIMIT + 1) return void stopAutoRun(`手前の戦場でも負けが続いたため停止しました`);
  const prev = r.backStage ?? stageId;
  r.backStage = prev > 1 && isCleared(prev - 1) ? prev - 1 : r.lastWin && r.lastWin !== stageId ? r.lastWin : prev > 1 ? prev - 1 : prev;
  r.phase = 'farm';
  r.farmCount = 0;
}

/** ドロップした装備を1つずつ記録。所持品に入らなかったもの（自動売却・所持枠超過）は applied.items と突き合わせて sold */
function collectDrops(r: AutoRun, server: FinishResponse | null) {
  const drops = server?.rewards?.equipment ?? [];
  if (!drops.length) return;
  const kept = new Map<string, number>();
  for (const it of server?.applied?.items ?? []) kept.set(`${it.defId}:${it.rarity}`, (kept.get(`${it.defId}:${it.rarity}`) ?? 0) + 1);
  for (const d of drops) {
    let sold = false;
    if (server?.applied) {
      const k = `${d.defId}:${d.rarity}`;
      const n = kept.get(k) ?? 0;
      if (n > 0) kept.set(k, n - 1);
      sold = n <= 0;
    }
    r.gains.items.push({ defId: d.defId, rarity: d.rarity, affixes: (d.affixes ?? []).map((a) => ({ id: a.id, value: a.value })), ...(d.set ? { set: d.set } : {}), ...(d.shin ? { shin: true } : {}), ...(sold ? { sold } : {}) });
  }
  // 上限を超えたら並から捨てる（数は drops に残る）
  if (r.gains.items.length > ITEMS_KEEP) {
    const i = r.gains.items.findIndex((x) => x.rarity === 'n');
    r.gains.items.splice(i >= 0 ? i : 0, r.gains.items.length - ITEMS_KEEP);
  }
}

/** 周回明けのまとめ演出（ui/haul-fx.ts）に渡す形にする。止まっていなければ null */
export function autoHaulOf(r: AutoRun | null = run): AutoHaul | null {
  if (!r?.endedAt) return null;
  const g = r.gains;
  return {
    charId: r.cfg.charId,
    battles: r.battles,
    wins: r.wins,
    losses: r.losses,
    minutes: Math.max(1, Math.round((r.endedAt - r.startedAt) / 60_000)),
    stopReason: r.stopReason,
    level: { before: r.start.level, after: Math.max(r.start.level, r.end.level) },
    power: r.start.power != null && r.end.power != null ? { before: r.start.power, after: r.end.power } : null,
    exp: g.exp,
    zeni: g.zeni,
    zaimoku: g.zaimoku,
    reidama: g.reidama,
    stars: g.stars,
    omamori: g.omamori,
    drops: g.items,
    shikigami: Object.entries(g.shiki).map(([enemyId, star]) => ({ enemyId, star })),
  };
}

/** 表示用の一行 */
export function autoRunLabel(r: AutoRun) {
  const lim = r.cfg.limit;
  const rest = lim.kind === 'count' ? `${r.battles}/${lim.n}戦` : lim.kind === 'until' ? `${r.battles}戦・${new Date(lim.at).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}まで` : `${r.battles}戦`;
  const streak = r.loseStreak ?? 0;
  const mode =
    r.backStage != null ? `手前で周回 ${r.farmCount}/${BACK_WINS}` : r.cfg.onLose === 'retry' && streak > 0 ? `再挑戦 ${streak}/${r.cfg.retryTimes ?? 3}敗` : r.phase === 'advance' ? '未踏へ挑戦' : '周回';
  return `${rest}・${r.wins}勝${r.losses}敗・${mode}`;
}
